'use client'

import Image from 'next/image'
import React, { useEffect, useState } from 'react'
import { RiInstagramLine } from 'react-icons/ri'

import { ArrowButton } from '@/components/ui/arrow-button'
import { CTAButton } from '@/components/ui/cta-button'
import { useCarouselScroll } from '@/hooks/useCarouselScroll'
import { cn } from '@/utilities/ui'

export type FeedBackground = 'cloud' | 'white' | 'black'

export interface FeedPost {
  id: string
  title: string
  url: string
  imageUrl: string
  imageAlt: string
  focalX: number
  focalY: number
}

interface FollowCardData {
  avatarUrl: string | null
  handle: string | null
  title: string | null
  description: string | null
  ctaLabel: string
  profileUrl: string | null
}

interface Props {
  background: FeedBackground
  posts: FeedPost[]
  followCard: FollowCardData
  label: string
}

const TRACK_COLOURS: Record<FeedBackground, string> = {
  cloud: 'bg-civo-cloud-dark',
  white: 'bg-civo-cloud',
  black: 'bg-civo-white/20',
}

// From xl, exactly four cards fill the gutters; cqw is the scroller's width, 72px the three gaps.
const TILE_SIZE =
  'h-[373px] w-[280px] md:h-[400px] md:w-[300px] xl:aspect-[3/4] xl:h-auto xl:w-[calc((100cqw-72px)/4)]'

// Thumb width is the visible share of the row, so it reads as "how much is left".
function useScrollThumb(ref: React.RefObject<HTMLDivElement | null>) {
  const [thumb, setThumb] = useState<{ progress: number; ratio: number | null }>({
    progress: 0,
    ratio: null,
  })

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => {
      const max = el.scrollWidth - el.clientWidth
      setThumb({
        progress: max > 0 ? el.scrollLeft / max : 0,
        ratio: el.scrollWidth > 0 ? Math.min(el.clientWidth / el.scrollWidth, 1) : 1,
      })
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [ref])

  return thumb
}

export function InstagramFeedCarousel({ background, posts, followCard, label }: Props) {
  const isDark = background === 'black'
  const itemCount = posts.length + 1

  // autoplayDelay 0 turns auto-advance off: with no pause control it would fail WCAG 2.2.2.
  const { scrollRef, activeIndex, isAtStart, isAtEnd } = useCarouselScroll({
    itemCount,
    autoplayDelay: 0,
  })
  const { progress, ratio } = useScrollThumb(scrollRef)

  // The hook's scrollToIndex targets raw offsetLeft, which would scroll the left gutter away.
  const scrollToCard = (index: number) => {
    const el = scrollRef.current
    if (!el) return
    const cards = el.querySelectorAll<HTMLElement>('[data-card]')
    const target = cards[Math.min(Math.max(index, 0), cards.length - 1)]
    if (!target || !cards[0]) return
    el.scrollTo({ left: target.offsetLeft - cards[0].offsetLeft, behavior: 'smooth' })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault()
      scrollToCard(activeIndex + (e.key === 'ArrowLeft' ? -1 : 1))
    }
  }

  const arrowClasses = cn(
    'size-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
    isDark
      ? 'border-civo-white text-civo-white focus-visible:outline-civo-white'
      : 'border-civo-black text-civo-black hover:bg-civo-black/10 focus-visible:outline-civo-black',
  )

  return (
    <div className="flex flex-col gap-8 md:gap-10 xl:gap-12">
      <div
        ref={scrollRef}
        role="region"
        aria-roledescription="carousel"
        aria-label={`${label} carousel`}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-hide',
          'scroll-pl-7 md:scroll-pl-9 [container-type:inline-size] xl:mx-16 xl:scroll-pl-0',
          'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2',
          isDark ? 'focus-visible:outline-civo-white' : 'focus-visible:outline-civo-black',
        )}
      >
        <ul role="list" className="flex w-max gap-3 px-7 md:gap-4 md:px-9 xl:gap-6 xl:px-0">
          <li data-card className={cn('shrink-0 snap-start', TILE_SIZE)}>
            <FollowCard {...followCard} isDark={isDark} />
          </li>
          {posts.map((post) => (
            <li key={post.id} data-card className={cn('shrink-0 snap-start', TILE_SIZE)}>
              <PostTile post={post} />
            </li>
          ))}
        </ul>
      </div>

      <p className="sr-only" aria-live="polite">
        {`Item ${activeIndex + 1} of ${itemCount}`}
      </p>

      {/* Visibility comes from the count, not a measurement, so the pager never pops in after
          hydration. Below xl the row usually bleeds; from xl four cards fit. */}
      <div
        className={cn(
          'flex items-center gap-6 px-7 md:px-9 xl:px-16',
          itemCount <= 4 && 'xl:hidden',
          ratio !== null && ratio >= 1 && 'invisible',
        )}
      >
        <div className={cn('relative h-0.5 flex-1 overflow-hidden', TRACK_COLOURS[background])}>
          <div
            className={cn(
              'absolute inset-y-0 left-0 transition-transform duration-150 ease-out',
              isDark ? 'bg-civo-white' : 'bg-civo-black',
            )}
            style={{
              width: `${(ratio ?? 0) * 100}%`,
              transform: `translateX(${ratio && ratio < 1 ? (progress * (1 - ratio) * 100) / ratio : 0}%)`,
            }}
          />
        </div>
        <div className="flex shrink-0 gap-3">
          <ArrowButton
            direction="left"
            backgroundColor="transparent"
            variant="minimal"
            animated
            className={arrowClasses}
            aria-label="Previous posts"
            onClick={() => scrollToCard(activeIndex - 1)}
            disabled={isAtStart}
          />
          <ArrowButton
            direction="right"
            backgroundColor="transparent"
            variant="minimal"
            animated
            className={arrowClasses}
            aria-label="Next posts"
            onClick={() => scrollToCard(activeIndex + 1)}
            disabled={isAtEnd}
          />
        </div>
      </div>
    </div>
  )
}

function FollowCard({
  avatarUrl,
  handle,
  title,
  description,
  ctaLabel,
  profileUrl,
  isDark,
}: FollowCardData & { isDark: boolean }) {
  return (
    <div
      className={cn(
        'flex size-full flex-col justify-between overflow-hidden rounded-[16px] p-6 md:p-7',
        isDark ? 'bg-civo-lime text-civo-black' : 'bg-civo-black text-civo-white',
      )}
    >
      <div className="flex items-start justify-between">
        {avatarUrl ? (
          <Image
            src={avatarUrl}
            alt=""
            width={56}
            height={56}
            className={cn(
              'size-14 rounded-full object-cover',
              !isDark && 'border-2 border-civo-lime',
            )}
          />
        ) : (
          <span />
        )}
        <RiInstagramLine aria-hidden="true" className="size-6" />
      </div>

      <div className="flex flex-col items-start gap-4">
        {handle && (
          <p
            className={cn(
              'body-copy-small mb-0 font-medium',
              isDark ? 'text-civo-black' : 'text-civo-lime',
            )}
          >
            {handle}
          </p>
        )}
        {title && <p className="display-compact-small mb-0">{title}</p>}
        {description && (
          <p
            className={cn(
              // From xl the card shrinks with the row; under 1184px of track (278px cards) it won't fit.
              'body-copy-small mb-0 xl:hidden xl:[@container(min-width:1184px)]:block',
              isDark ? 'text-civo-black/70' : 'text-civo-white/70',
            )}
          >
            {description}
          </p>
        )}
        {profileUrl && (
          <CTAButton
            variant={isDark ? 'black' : 'green'}
            href={profileUrl}
            newTab
            surface={isDark ? 'lime' : 'black'}
          >
            {ctaLabel}
          </CTAButton>
        )}
      </div>
    </div>
  )
}

function PostTile({ post }: { post: FeedPost }) {
  return (
    <a
      href={post.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative flex size-full flex-col justify-end overflow-hidden rounded-[16px] p-5 text-civo-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-civo-lime"
    >
      <Image
        src={post.imageUrl}
        alt={post.imageAlt}
        fill
        sizes="(min-width: 1280px) 25vw, 300px"
        style={{ objectPosition: `${post.focalX}% ${post.focalY}%` }}
        className="object-cover transition-transform duration-500 ease-out group-hover:scale-105 group-focus-visible:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100 motion-reduce:group-focus-visible:scale-100"
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-civo-black/35 to-transparent to-25%"
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 hidden bg-gradient-to-t from-civo-black/70 to-transparent to-50% [@media(hover:none)]:block"
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-civo-black/55 opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100"
      />
      <RiInstagramLine aria-hidden="true" className="absolute right-5 top-5 size-5" />
      <span className="subheading-xsmall relative opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100 [@media(hover:none)]:opacity-100">
        {post.title}
        <span className="sr-only"> (opens Instagram in a new tab)</span>
      </span>
    </a>
  )
}
